import React, { useState, useEffect } from 'react';
import Footer from '../components/layout/Footer';
import { getAllSchedules } from '../services/api';

const Schedule = () => {
  window.scrollTo(0, 0);

  const days = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];

  const dayDisplayMap = {
    T2: 'Thứ 2',
    T3: 'Thứ 3',
    T4: 'Thứ 4',
    T5: 'Thứ 5',
    T6: 'Thứ 6',
    T7: 'Thứ 7',
    CN: 'Chủ Nhật',
  };

  const today = new Date().getDay();
  const todayStr = days[today === 0 ? 6 : today - 1];

  const [selectedDay, setSelectedDay] = useState(todayStr);
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [schedule, setSchedule] = useState([]);
  const [times, setTimes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const response = await getAllSchedules();
        if (response && response.data) {
          setSchedule(response.data);

          const uniqueTimes = [...new Set(response.data.map(s => s.time_range))].sort((a, b) => {
            const [aHour, aMin] = a.split(' - ')[0].split(':').map(Number);
            const [bHour, bMin] = b.split(' - ')[0].split(':').map(Number);
            return aHour !== bHour ? aHour - bHour : aMin - bMin;
          });
          setTimes(uniqueTimes);
        }
      } catch (error) {
        console.error('Error fetching schedule:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSchedule();
  }, []);

  const getScheduleCell = (day, time) => {
    return schedule.filter(s => {
      const scheduleDays = s.schedule_day.split('/');
      return scheduleDays.includes(day) && s.time_range === time && (selectedGrade === 'all' || s.grade === selectedGrade);
    }).map((cls, idx) => (
      <div
        key={idx}
        className="border rounded p-2 mb-2 text-sm text-gray-700 bg-white shadow"
      >
        <div className="font-semibold">{cls.teacher_name}</div>
        <div>📍 Phòng: {cls.room}</div>
        <div>🎓 Khối: {cls.grade}</div>
        <div>📚 Môn: {cls.major}</div>
      </div>
    ));
  };

  const hasScheduleToday = times.some(time => getScheduleCell(selectedDay, time).length > 0);

  if (loading) return <div className="p-8 text-center">Loading schedule...</div>;

  return (
    <div className="bg-gray-100 min-h-screen py-8">
      <section className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-20">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-6">Lịch Học Hằng Tuần</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Xem chi tiết lịch học từng khối lớp, giúp bạn dễ dàng theo dõi và sắp xếp thời gian học tập hiệu quả
          </p>

        </div>
      </section>

      {/* Mobile View */}
      <div className="lg:hidden px-4">
        <div className="text-center mb-4">
          <label className="mr-2 font-semibold">Chọn khối:</label>
          <select value={selectedGrade} onChange={(e) => setSelectedGrade(e.target.value)} className="border rounded px-3 py-1 mr-4">
            <option value="all">Tất cả</option>
            {[7, 8, 9, 10, 11, 12].map(grade => (
              <option key={grade} value={String(grade)}>
                Khối {grade}
              </option>
            ))}
          </select>
          <label className="mr-2 font-semibold">Chọn ngày:</label>
          <select value={selectedDay} onChange={(e) => setSelectedDay(e.target.value)} className="border rounded px-3 py-1 mr-4">
            {days.map(day => (
              <option key={day} value={day}>
                {day === todayStr ? `${dayDisplayMap[day]} (Hôm nay)` : dayDisplayMap[day]}
              </option>
            ))}
          </select>
        </div>

        {hasScheduleToday ? (
          times
            .filter(time => getScheduleCell(selectedDay, time).length > 0)
            .map(time => (
              <div key={time}>
                <div className="font-bold text-blue-700 text-center my-2">{time}</div>
                {getScheduleCell(selectedDay, time)}
              </div>
            ))
        ) : (
          <div className="text-center text-gray-600 font-medium">Không có lịch học hôm nay</div>
        )}
      </div>

      {/* Desktop View */}
      <div className="hidden lg:block overflow-x-auto px-4">
        <div className="flex items-center justify-center mb-4">
          <label className="mr-2 font-semibold">Chọn khối:</label>
          <select value={selectedGrade} onChange={(e) => setSelectedGrade(e.target.value)} className="border rounded px-3 py-1">
            <option value="all">Tất cả</option>
            {[7, 8, 9, 10, 11, 12].map(grade => (
              <option key={grade} value={String(grade)}>
                Khối {grade}
              </option>
            ))}
          </select>
        </div>
        <table className="min-w-full bg-white border border-gray-300 text-center">
          <thead className="bg-blue-100">
            <tr>
              <th className="border p-2">Thời gian</th>
              {days.map(day => (
                <th key={day} className={`border p-2 ${day === todayStr ? 'bg-yellow-100 font-bold text-yellow-800' : ''}`}>
                  {dayDisplayMap[day]}{day === todayStr ? ' (Hôm nay)' : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {times
              .filter(time => days.some(day => getScheduleCell(day, time).length > 0))
              .map(time => (
                <tr key={time}>
                  <td className="border p-2 font-semibold bg-blue-50">{time}</td>
                  {days.map(day => (
                    <td key={day + time} className={`border p-2 align-top min-w-[120px] ${day === todayStr ? 'bg-yellow-50' : ''}`}>
                      {getScheduleCell(day, time)}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Footer />
    </div>
  );
};

export default Schedule;